import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { supabase, TowJob, Profile } from '../lib/supabase'
import { 
  ArrowLeft, 
  Loader2, 
  CreditCard, 
  CheckCircle2, 
  AlertCircle,
  Receipt,
  Truck,
  Phone,
  User
} from 'lucide-react'

export default function TowerPaymentApprovalPage() {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [job, setJob] = useState<TowJob | null>(null)
  const [tower, setTower] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [notes, setNotes] = useState('')

  useEffect(() => {
    if (!jobId) return

    const loadJobAndTower = async () => {
      const { data: jobData } = await supabase
        .from('tow_jobs')
        .select('*')
        .eq('id', jobId)
        .single()

      if (jobData) {
        setJob(jobData as TowJob)
        
        if (jobData.selected_tower_id || jobData.operator_id) {
          const { data: towerData } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', jobData.selected_tower_id || jobData.operator_id)
            .single()
          
          setTower(towerData as Profile)
        }
      }
      
      setLoading(false)
    }

    loadJobAndTower()
  }, [jobId])

  const handleApprovePayment = async () => {
    if (!job || !profile) return

    setProcessing(true)
    setError('')

    try {
      // Get tower ID from job
      const towerIdToUse = job.selected_tower_id || job.operator_id
      if (!towerIdToUse) {
        setError('No tower found for this job')
        setProcessing(false)
        return
      }

      // Always generate payment reference and document number
      const autoPaymentRef = `PAY-${Date.now().toString().slice(-8)}`
      const autoDocNumber = `DOC-${Date.now().toString().slice(-8)}`

      // Try to create transaction record first
      const { error: transactionError } = await supabase
        .from('tower_garage_transactions')
        .insert({
          job_id: job.id,
          tower_id: towerIdToUse,
          garage_id: profile.id,
          amount: paymentAmount,
          payment_method: 'cash',
          payment_reference: autoPaymentRef,
          document_number: autoDocNumber,
          notes: notes,
          paid_at: new Date().toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          auto_generated_ref: true,
          auto_generated_doc: true
        })

      if (transactionError) {
        console.log('Transaction record failed:', transactionError.message)
        // Add a note to job with payment details (always in new format)
        const paymentNote = `PAID|${autoPaymentRef}|${autoDocNumber}|${new Date().toISOString()}|${notes || 'Payment approved'}`
        
        await supabase
          .from('tow_jobs')
          .update({
            diagnosis_notes: paymentNote
          })
          .eq('id', job.id)
      }

      setSuccess(true)
      setTimeout(() => {
        navigate('/vendor')
      }, 2000)
    } catch (error) {
      console.error('Error approving payment:', error)
      setError(`Failed to approve payment: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }

    setProcessing(false)
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 size={48} className="spin" style={{ color: 'var(--red)', marginBottom: '1rem' }} />
          <div style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--muted)' }}>Loading payment details...</div>
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <AlertCircle size={48} style={{ color: 'var(--red)', marginBottom: '1rem' }} />
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
            Job Not Found
          </div>
          <button
            onClick={() => navigate('/vendor')}
            className="btn btn-primary"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const paymentAmount = job.total_amount || job.service_price || 0

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '1rem 1.5rem',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={() => navigate('/vendor')}
          style={{
            padding: '0.5rem',
            background: 'var(--bg)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          <ArrowLeft size={20} style={{ color: 'var(--text)' }} />
        </button>
        <div>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)' }}>
            Approve Tower Payment
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
            Review and approve payment for towing service
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '1.5rem' }}>
        {success ? (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <CheckCircle2 size={80} style={{ color: 'var(--green)', marginBottom: '1.5rem' }} />
              <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Payment Approved!
              </div>
              <div style={{ fontSize: '1.1rem', color: 'var(--green)', fontWeight: 600, marginBottom: '0.5rem' }}>
                KES {paymentAmount.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--muted)', marginBottom: '1rem' }}>
                Tower has been notified of the payment
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                Redirecting to dashboard...
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Job Summary Card */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>
                    JOB REFERENCE
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>
                    {job.reference}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>
                    AMOUNT TO PAY
                  </div>
                  <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--green)' }}>
                    KES {paymentAmount.toLocaleString()}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.9rem' }}>
                <div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    SERVICE
                  </div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {job.service_type === 'towing' ? '🚚 Towing' : job.service_type.replace('_', ' ')}
                  </div>
                </div>
                <div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    DELIVERY DATE
                  </div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {job.completed_at ? new Date(job.completed_at).toLocaleDateString() : '--'}
                  </div>
                </div>
                <div style={{ gridColumn: 'span  2' }}>
                  <div style={{ color: 'var(--muted)', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    PICKUP LOCATION
                  </div>
                  <div style={{ color: 'var(--text)', fontWeight: 600 }}>
                    {job.pickup_address}
                  </div>
                </div>
              </div>
            </div>

            {/* Tower Info Card */}
            {tower && (
              <div className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '1rem' }}>
                  TOWER OPERATOR
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'var(--red-light)',
                    color: 'var(--red)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700
                  }}>
                    {tower.full_name?.charAt(0) || 'T'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                      {tower.full_name || 'Tower Operator'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                      {tower.truck_plate && `Plate: ${tower.truck_plate}`}
                    </div>
                    <button
                      onClick={() => tower.phone && (window.location.href = `tel:${tower.phone}`)}
                      className="btn btn-outline btn-sm"
                    >
                      <Phone size={16} />
                      Call Tower
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="login-error" style={{ marginBottom: '1rem' }}>
                <AlertCircle size={20} />
                {error}
              </div>
            )}

            {/* Notes */}
            <div className="card" style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.5rem', display: 'block' }}>
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Add any notes about this payment..."
                rows={3}
                className="form-input"
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {/* Approve Button */}
            <button
              onClick={handleApprovePayment}
              disabled={processing}
              className="btn btn-primary"
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.75rem',
                padding: '1.25rem',
                fontSize: '1.1rem',
                marginTop: '1rem'
              }}
            >
              {processing ? <Loader2 size={24} className="spin" /> : <CheckCircle2 size={24} />}
              {processing ? 'Processing...' : 'Approve Payment'}
            </button>

            {/* Info Box */}
            <div style={{ 
              marginTop: '1rem',
              padding: '1rem',
              background: 'var(--green-light)',
              borderRadius: '12px',
              border: '1px solid var(--green)',
              fontSize: '0.9rem',
              color: 'var(--green)',
              lineHeight: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <CheckCircle2 size={20} />
              <span>Payment will be recorded and the tower will be notified immediately</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
